
/**
 * Mail Order Batch: key in one envelope after another with the keyboard.
 * Each envelope is its own payment (one form token, new after every save)
 * stamped with this batch's id; the batch itself is only a label. A check,
 * money order or cash is recorded; a card or eCheck written on the form is
 * charged at once through Accept.js (the numbers live only in AcceptFields'
 * uncontrolled inputs) and is refunded, not voided.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import {
  batchAction, deleteBatch, deskSearch, getBatch, getBatchSummary, getMember, getPaymentPlans, getPaymentsConfig, listBatches, openBatch,
  postOfflinePayment, postPhonePayment, postVoid, type DeskPlan, type HeldForReview, type MatchCandidate, type PaymentResult,
  type RecentCharge, type SearchHit,
} from './api'
import { AcceptFields, type AcceptHandle } from './AcceptFields'
import type { GatewayConfig } from './acceptJs'
import { batchDetailDefault, batchLinesBody, canChargeEntry, canSaveEntry, depositSlipText, entryVoidState, keepToken, prefillLine, refundItem, returnedAfterDepositText, runningTotals, type BatchEntry, type Envelope, type Line } from './batch'
import { MoneyDialog } from './MoneyDialog'
import { batchHref, memberHref, paymentHref } from './links'
import { filtersFromParams, filtersToParams, openedAtText, STATUS_WORD, type BatchFilters, type BatchStatusFilter } from './batchList'
import { HelpLink } from './HelpLink'
import {
  balance, centsToInput, detailField, dollars, isHeldForReview, isManual, linesTotal, methodDetailBody, offerLabel, parseMoney, personKey,
  phoneFailure, recentChargeText, saveLabel, unconfirmedText, type ManualMethod, type Method,
} from './payment'
import { RecipientPicker } from './RecipientPicker'
import { blankRcpt, newPersonParts, recipientReady, useRecipientGates } from './recipient'
import { highlight, moveSelection, resultsAreCurrent, searchReady } from './search'
import './print.css'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[44px] w-full rounded-lg border border-ink-400 bg-ink-800 px-3 text-[15px] text-paper ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'
const panel = 'flex flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 p-5'
const h2 = 'text-[12px] font-bold uppercase tracking-widest text-paper/60'
const btn = `inline-flex min-h-[44px] items-center justify-center rounded-lg border border-ink-400 px-4 text-[14px] font-bold text-paper hover:bg-ink-600 ${focus}`
const btnSm = `inline-flex min-h-[34px] items-center justify-center rounded-lg border border-ink-400 px-3 text-[13px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const btnPrimary = `inline-flex min-h-[46px] items-center justify-center rounded-lg bg-paper px-5 font-bold text-ink-900 disabled:cursor-not-allowed disabled:opacity-45 ${focus}`
const kbd = 'rounded border border-ink-400 px-1.5 font-mono text-[11px] text-paper/70'

const METHODS: { method: Method; label: string; ref: string }[] = [
  { method: 'check', label: 'Check', ref: 'Check number' },
  { method: 'money_order', label: 'Money order', ref: 'Serial number' },
  { method: 'cash', label: 'Cash', ref: 'Receipt no. (optional)' },
  { method: 'card', label: 'Card', ref: '' },
  { method: 'bank', label: 'eCheck', ref: '' },
]
const METHOD_WORD: Record<string, string> = { check: 'Check', money_order: 'Money order', cash: 'Cash', card: 'Card', bank: 'eCheck' }
const METHOD_PLURAL: Record<string, string> = { check: 'Checks', money_order: 'Money orders', cash: 'Cash', card: 'Cards', bank: 'eChecks' }
const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking' }, { value: 'savings', label: 'Savings' }, { value: 'businessChecking', label: 'Business checking' },
]

/** The card form could not turn the numbers into a token: nothing was sent. */
class TokenError extends Error {}

const todayIso = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function errorText(e: unknown, what = 'save'): string {
  if (e instanceof ApiError) {
    const d = e.detail && typeof e.detail === 'object' ? (e.detail as { detail?: unknown }).detail : e.detail
    if (typeof d === 'string') return d
    if (Array.isArray(d)) return (d[0] as { msg?: string } | undefined)?.msg ?? 'Check the form and try again.'
    if (d && typeof d === 'object' && (d as { code?: string }).code === 'possible_duplicate') {
      return 'This looks like a new person who may already be on file. Open the full payment screen to check.'
    }
    if (e.status === 429) return 'Too many tries. Wait a few minutes and try again.'
  }
  return `Could not ${what}. Check your connection and try again.`
}

const titleName = (plans: DeskPlan[], slug: string): string => plans.find((p) => p.publication_slug === slug)?.publication_name ?? slug

export function BatchPage() {
  const id = Number(useParams().id)
  return Number.isFinite(id) && id > 0 ? <BatchScreen id={id} /> : <BatchStart />
}

function BatchStart() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const filters = filtersFromParams(params)
  const setFilter = (patch: Partial<BatchFilters>) => setParams(filtersToParams({ ...filters, ...patch }), { replace: true })
  const list = useQuery({ queryKey: ['desk-batches', filters], queryFn: () => listBatches(filters), placeholderData: keepPreviousData, refetchOnMount: 'always' })
  const start = useMutation({
    mutationFn: openBatch,
    onSuccess: (b) => { void qc.invalidateQueries({ queryKey: ['desk-batches'] }); navigate(batchHref(b.id)) },
  })
  const rows = list.data?.batches ?? []
  const filtered = filters.status !== 'all' || !!filters.from || !!filters.to

  return (
    <main className="flex min-w-0 flex-1 flex-col gap-5 px-4 pb-16 pt-10 md:px-12">
      <header className="flex flex-wrap items-end justify-between gap-5">
        <div className="flex flex-col gap-1.5">
          <h1 className="font-serif text-4xl font-medium tracking-tight">Mail Order Batch</h1>
          <p className="text-paper/70">Checks, money orders and cash from the post, keyed and deposited together.</p>
          <HelpLink page="batch" />
        </div>
        <button type="button" className={btnPrimary} disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Starting…' : 'Start a new batch'}
        </button>
      </header>
      {start.isError && <p role="alert" className="text-radar-red">Could not start a batch. Check your connection and try again.</p>}

      <section className={panel} aria-labelledby="batches-h">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 id="batches-h" className={h2}>Batches</h2>
          <div className="flex flex-wrap items-end gap-3">
            <label className={lbl}>Status
              <select className={inp} value={filters.status} onChange={(e) => setFilter({ status: e.target.value as BatchStatusFilter })}>
                <option value="all">All</option><option value="open">Open</option><option value="closed">Closed</option><option value="deposited">Deposited</option><option value="archived">Archived</option>
              </select>
            </label>
            <label className={lbl}>Opened from
              <input type="date" className={inp} value={filters.from} max={filters.to || undefined} onChange={(e) => setFilter({ from: e.target.value })} />
            </label>
            <label className={lbl}>to
              <input type="date" className={inp} value={filters.to} min={filters.from || undefined} onChange={(e) => setFilter({ to: e.target.value })} />
            </label>
            {filtered && <button type="button" className={btn} onClick={() => setParams({}, { replace: true })}>Clear</button>}
          </div>
        </div>
        {list.isError && <p role="alert" className="text-radar-red">Could not load the batches. Reload the page.</p>}
        {list.isPending && <p className="text-paper/70">Loading…</p>}
        {list.isSuccess && rows.length === 0 && (
          <p className="text-paper/70">{filters.status === 'archived' ? 'No archived batches match these filters.' : filtered ? 'No batches match these filters.' : 'No batches yet. Start one when the post comes in.'}</p>
        )}
        {rows.length > 0 && (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-[14px]">
              <thead><tr className="text-[11px] uppercase tracking-widest text-paper/60">
                <th scope="col" className="p-2 text-left">Batch</th><th scope="col" className="p-2 text-left">Status</th>
                <th scope="col" className="p-2 text-left">Opened</th><th scope="col" className="p-2 text-right">Envelopes</th>
                <th scope="col" className="p-2 text-right">Total</th><th scope="col" className="p-2"><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} className="border-t border-ink-500">
                    <td className="p-2 font-mono font-semibold"><Link className={`underline-offset-2 hover:underline ${focus}`} to={batchHref(b.id)}>{b.label}</Link></td>
                    <td className={`p-2 font-semibold ${b.status === 'open' ? 'text-amber-glow' : b.status === 'deposited' ? 'text-paper/60' : ''}`}>{STATUS_WORD[b.status]}{b.archived_at ? ' · archived' : ''}</td>
                    <td className="p-2 text-paper/70">{openedAtText(b.opened_at)}{b.opened_by_email ? ` · ${b.opened_by_email}` : ''}</td>
                    <td className="p-2 text-right font-mono">{b.entries}</td>
                    <td className="p-2 text-right font-mono">{dollars(b.amount_cents)}</td>
                    <td className="p-2 text-right"><Link className={btnSm} to={batchHref(b.id)}>{b.status === 'open' ? 'Resume' : 'Review'}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length >= 200 && <p className="mt-2 text-[12px] text-paper/60">Showing the 200 most recent. Narrow the dates to see older batches.</p>}
          </div>
        )}
      </section>
    </main>
  )
}

function BatchScreen({ id }: { id: number }) {
  const qc = useQueryClient()
  const batchQ = useQuery({ queryKey: ['desk-batch', id], queryFn: () => getBatch(id) })
  const cfgQ = useQuery({ queryKey: ['desk-pay-config'], queryFn: getPaymentsConfig })
  const phoneOn = !!cfgQ.data?.phone_payments_enabled
  const methods = METHODS.filter((m) => (m.method === 'card' ? phoneOn : m.method === 'bank' ? phoneOn && !!cfgQ.data?.echeck_enabled : true))
  const plansQ = useQuery({ queryKey: ['desk-pay-plans'], queryFn: getPaymentPlans })
  const plans: DeskPlan[] = useMemo(() => plansQ.data?.plans ?? [], [plansQ.data])
  const batch = batchQ.data
  const isOpen = !!batch && !batch.closed_at
  const isClosed = !!batch?.closed_at
  const isArchived = !!batch?.archived_at
  // Nothing was ever keyed in (a voided entry still counts as keyed).
  const isEmpty = !!batch && batch.entries.length === 0
  const navigate = useNavigate()

  const whoRef = useRef<HTMLInputElement>(null)
  const refRef = useRef<HTMLInputElement>(null)
  const amountRef = useRef<HTMLInputElement>(null)
  const [raw, setRaw] = useState('')
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(0)
  const [picked, setPicked] = useState<SearchHit | null>(null)
  const [method, setMethod] = useState<Method>('check')
  const manual = isManual(method)
  const [reference, setReference] = useState('')
  const [detail, setDetail] = useState('')
  const pickMethod = (m: Method) => { setMethod(m); setDetail(isManual(m) ? batchDetailDefault(m) : '') }
  // Card and eCheck: what the form says besides the numbers, and how the last charge went.
  const acceptRef = useRef<AcceptHandle>(null)
  const [billZip, setBillZip] = useState('')
  const [nameOnAccount, setNameOnAccount] = useState('')
  const [accountType, setAccountType] = useState('checking')
  const [signed, setSigned] = useState(false)
  const [keepCard, setKeepCard] = useState(false)
  const [recent, setRecent] = useState<RecentCharge[] | null>(null)
  const [held, setHeld] = useState<HeldForReview | null>(null)
  const [unconfirmed, setUnconfirmed] = useState<{ txn: string | null; invoice: string | null } | null>(null)
  const [amountEdit, setAmountEdit] = useState<string | null>(null)
  const [linesEdit, setLinesEdit] = useState<Line[] | null>(null)
  const formToken = useRef(crypto.randomUUID())
  const lastTry = useRef<Envelope | null>(null)
  const [problem, setProblem] = useState('')
  const [saved, setSaved] = useState('')
  const inFlight = useRef(false)
  const [voiding, setVoiding] = useState<BatchEntry | null>(null)
  const [refunding, setRefunding] = useState<BatchEntry | null>(null)
  // The server's own duplicate check on a new gift recipient, and the
  // "these are all different people" answer (for exactly these people).
  const [dupList, setDupList] = useState<MatchCandidate[] | null>(null)
  const dupOk = useRef<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => { setQ(raw.trim()); setSel(0) }, 200)
    return () => clearTimeout(t)
  }, [raw])

  const ready = !picked && searchReady(q)
  const searchQ = useQuery({
    queryKey: ['desk-search', q, '', ''],
    queryFn: async () => ({ ...(await deskSearch({ q })), publication: '', status: '' }),
    enabled: ready,
    placeholderData: keepPreviousData,
  })
  const hits: SearchHit[] = ready ? (searchQ.data?.items ?? []).slice(0, 6) : []
  const current = ready && resultsAreCurrent({ raw, q, title: '', status: '', data: searchQ.data })

  const recordQ = useQuery({ queryKey: ['desk-member', picked?.subscriber_id], queryFn: () => getMember(picked!.subscriber_id), enabled: !!picked })
  // What the member's record suggests, until the operator edits the line or the amount.
  const prefill = useMemo(() => (picked && recordQ.data && plansQ.data ? prefillLine(recordQ.data, plans) : null), [picked, recordQ.data, plansQ.data, plans])
  const lines: Line[] = linesEdit ?? (prefill ? [prefill] : [])
  const amount = amountEdit ?? (prefill && linesEdit === null ? centsToInput(prefill.price_cents) : '')
  const setAmount = (v: string) => setAmountEdit(v)
  const setLines = (f: (ls: Line[]) => Line[]) => {
    setAmountEdit((a) => a ?? amount)
    setLinesEdit(f(lines))
  }

  const amountCents = parseMoney(amount)
  const bal = balance(method, amountCents, lines)
  const gates = useRecipientGates(lines.map((l) => (l.for === 'gift' ? l.rcpt ?? null : null)), picked?.subscriber_id ?? null)
  const giftsReady = lines.every((l, i) => l.for !== 'gift' || (!!l.rcpt && recipientReady(l.rcpt, gates[i])))
  const peopleKey = personKey(...lines.flatMap((l) => (l.for === 'gift' && l.rcpt ? newPersonParts(l.rcpt) : [])))
  const gw = useQuery({
    queryKey: ['desk-pay-gateway'],
    queryFn: () => api.get<GatewayConfig>('/api/public/gateway-config'),
    enabled: !manual,
    staleTime: Infinity,
  })
  const chargeCents = linesTotal(lines)
  const entryOk = isOpen && !held && !unconfirmed && (manual
    ? canSaveEntry({ picked: !!picked, method, reference, amountCents, lines, giftsReady })
    : canChargeEntry({ picked: !!picked, method, lines, giftsReady, billZip, nameOnAccount, signed }))
  const canSave = entryOk && !dupList
  const totals = batch ? runningTotals(batch) : { count: 0, amountCents: 0 }
  const titles = useMemo(() => {
    const seen = new Map<string, string>()
    plans.forEach((p) => seen.set(p.publication_slug, p.publication_name))
    return [...seen].map(([slug, name]) => ({ slug, name }))
  }, [plans])

  function choose(h: SearchHit) {
    formToken.current = crypto.randomUUID()
    lastTry.current = null
    setPicked(h)
    setRaw(h.name)
    setLinesEdit(null)
    setAmountEdit(null)
    setDupList(null)
    dupOk.current = null
    setProblem('')
    setSaved('')
    setRecent(null)
    if (manual) setTimeout(() => (method === 'cash' ? amountRef : refRef).current?.focus(), 0)
  }
  function clearEntry() {
    formToken.current = crypto.randomUUID()
    lastTry.current = null
    setPicked(null); setRaw(''); setQ(''); setSel(0); setReference(''); setAmountEdit(null); setLinesEdit(null)
    pickMethod('check'); setProblem(''); setDupList(null); dupOk.current = null
    setBillZip(''); setNameOnAccount(''); setAccountType('checking'); setSigned(false); setKeepCard(false)
    setRecent(null); setHeld(null); setUnconfirmed(null)
    setTimeout(() => whoRef.current?.focus(), 0)
  }

  const save = useMutation({
    // chargeAgain: staff saw a recent charge for this member and mean to charge again.
    mutationFn: async (opts?: { chargeAgain?: boolean }): Promise<PaymentResult | HeldForReview> => {
      const common = {
        form_token: formToken.current, payer_subscriber_id: picked!.subscriber_id, lines: batchLinesBody(lines), batch_id: id,
        confirm_not_duplicate: dupOk.current !== null && dupOk.current === peopleKey,
      }
      if (isManual(method)) {
        return postOfflinePayment({
          ...common, method, reference: reference.trim(), received_date: todayIso(), amount_cents: amountCents,
          ...methodDetailBody(method, { [detailField(method).key]: detail }),
        })
      }
      const nonce = await acceptRef.current!.tokenize({ zip: billZip, fullName: nameOnAccount, accountType })
        .catch((e: unknown) => { throw new TokenError(e instanceof Error ? e.message : 'The card form could not read those details.') })
      return postPhonePayment({
        ...common, instrument: method,
        opaque_data: { dataDescriptor: nonce.descriptor, dataValue: nonce.value },
        bill_zip: billZip.trim(), keep_card_for_auto_renew: method === 'card' && keepCard,
        ...(opts?.chargeAgain ? { confirm_charge_again: true } : {}),
        ...(method === 'bank' ? { account_type: accountType, signed_form_authorization: true } : {}),
      })
    },
    onSettled: () => { inFlight.current = false },
    onSuccess: (r) => {
      if (isHeldForReview(r)) { setProblem(''); setHeld(r); return }
      setSaved(method === 'card' ? `Charged ${dollars(chargeCents)} to ${picked!.name}'s card.`
        : method === 'bank' ? `Debited ${dollars(chargeCents)} from ${picked!.name}'s bank account.`
          : `Saved ${METHOD_WORD[method].toLowerCase()} ${dollars(amountCents)} from ${picked!.name}.`)
      clearEntry()
      void qc.invalidateQueries({ queryKey: ['desk-batch', id] })
      void qc.invalidateQueries({ queryKey: ['desk-member'] })
    },
    onError: (e) => {
      setSaved('')
      if (e instanceof TokenError) { setProblem(e.message); return }
      if (!isManual(method)) {
        // Only a definite answer from us means nothing was charged.
        const f = e instanceof ApiError ? phoneFailure(e.status, e.detail) : phoneFailure(null, undefined)
        if (f?.kind === 'unconfirmed') { setProblem(''); setUnconfirmed({ txn: f.txn, invoice: f.invoice }); return }
        if (f?.kind === 'message') { setProblem(f.text); return }
      }
      const d = e instanceof ApiError && e.detail && typeof e.detail === 'object'
        ? (e.detail as { detail?: { code?: string; message?: string; candidates?: MatchCandidate[]; recent?: RecentCharge[] } }).detail : undefined
      if (d && typeof d === 'object' && d.code === 'possible_duplicate') { setProblem(''); setDupList(d.candidates ?? []); return }
      if (d && typeof d === 'object' && d.code === 'possible_duplicate_charge') { setProblem(''); setRecent(d.recent ?? []); return }
      if (d && typeof d === 'object' && d.code === 'declined') {
        setProblem(`${d.message ?? 'The card was declined.'} Nothing was charged. Set the form aside and contact the member.`)
        return
      }
      setProblem(errorText(e))
    },
  })
  /** `differentPeople`: staff answered the server's duplicate check for these people.
   *  `chargeAgain`: staff saw this member's recent charge and mean to charge again. */
  function trySave(differentPeople = false, chargeAgain = false) {
    if (!(differentPeople ? entryOk : canSave) || inFlight.current || save.isPending) return
    if (differentPeople) { dupOk.current = peopleKey; setDupList(null) }
    inFlight.current = true
    setRecent(null)
    if (isManual(method)) {
      const env: Envelope = { memberId: picked!.subscriber_id, method, reference, amountCents, lines, detail }
      if (!keepToken(lastTry.current, env)) formToken.current = crypto.randomUUID()
      lastTry.current = env
    } else {
      // Every charge is a fresh attempt: the server's own guards catch a repeat.
      formToken.current = crypto.randomUUID()
      lastTry.current = null
    }
    save.mutate(chargeAgain ? { chargeAgain: true } : undefined)
  }

  function onWhoKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!picked && hits.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel((i) => moveSelection(i, 1, hits.length)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSel((i) => moveSelection(i, -1, hits.length)); return }
      if (e.key === 'Enter' && current && hits[sel]) { e.preventDefault(); choose(hits[sel]); return }
    }
    if (e.key === 'Enter') { e.preventDefault(); trySave() }
  }
  function onEntryKey(e: React.KeyboardEvent<HTMLElement>) {
    if (e.key === 'Escape') { e.preventDefault(); clearEntry(); return }
    const t = e.target as HTMLElement
    if (e.key === 'Enter' && t.tagName === 'INPUT' && t !== whoRef.current) { e.preventDefault(); trySave() }
  }

  const patchLine = (i: number, p: Partial<Line>) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...p } : l)))
  function pickTitle(i: number, slug: string) {
    const offers = plans.filter((p) => p.publication_slug === slug)
    const p = offers.find((o) => o.term_months === 12) ?? offers[0]
    patchLine(i, { slug, plan_id: p?.plan_id ?? '', price_cents: p?.amount_cents ?? 0 })
  }
  function pickPlan(i: number, slug: string, planId: string) {
    const p = plans.find((o) => o.publication_slug === slug && o.plan_id === planId)
    patchLine(i, { plan_id: planId, price_cents: p?.amount_cents ?? 0 })
  }

  const act = useMutation({
    mutationFn: (what: 'close' | 'reopen' | 'deposited' | 'archive' | 'unarchive') => batchAction(id, what),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['desk-batch', id] }); void qc.invalidateQueries({ queryKey: ['desk-batch-summary', id] }); void qc.invalidateQueries({ queryKey: ['desk-batches'] })
    },
  })
  const remove = useMutation({
    mutationFn: () => deleteBatch(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['desk-batches'] }); qc.removeQueries({ queryKey: ['desk-batch', id] }); navigate(batchHref()) },
  })
  const confirmDelete = () => { if (batch && window.confirm(`Delete batch ${batch.label}? It has nothing in it and will be removed from the list.`)) remove.mutate() }
  const refreshAfterVoid = () => {
    void qc.invalidateQueries({ queryKey: ['desk-batch', id] }); void qc.invalidateQueries({ queryKey: ['desk-batch-summary', id] }); void qc.invalidateQueries({ queryKey: ['desk-member'] })
  }
  const summaryQ = useQuery({ queryKey: ['desk-batch-summary', id], queryFn: () => getBatchSummary(id), enabled: isClosed })

  if (batchQ.isError) {
    return (
      <main className="flex-1 px-4 pt-10 md:px-12">
        <h1 className="font-serif text-4xl font-medium tracking-tight">Mail Order Batch</h1>
        <p role="alert" className="mt-4 text-radar-red">That batch could not be loaded. <Link className="underline" to={batchHref()}>Back to batches</Link></p>
      </main>
    )
  }

  return (
    <main className="flex min-w-0 flex-1 flex-col gap-5 px-4 pb-16 pt-10 md:px-12">
      <Link to={batchHref()} className={`no-print w-fit text-[13px] ${focus}`}>← All batches</Link>
      <header className="flex flex-wrap items-end justify-between gap-5">
        <div className="flex flex-col gap-1.5">
          <h1 className="font-serif text-4xl font-medium tracking-tight">Mail Order Batch</h1>
          <p className="text-paper/70">
            {batch ? `Batch ${batch.label}` : 'Loading…'}{batch?.deposited_at ? ' · deposited' : isClosed ? ' · closed' : ''}{isArchived ? ' · archived' : ''} · payments from the post
          </p>
          <HelpLink page="batch" />
        </div>
        <div className="flex flex-wrap items-center gap-7">
          <div className="flex flex-col"><span className="font-mono text-2xl" data-testid="batch-count">{totals.count}</span><span className="text-[12px] text-paper/60">Entered</span></div>
          <div className="flex flex-col"><span className="font-mono text-2xl" data-testid="batch-total">{dollars(totals.amountCents)}</span><span className="text-[12px] text-paper/60">Batch total</span></div>
          {isOpen && <button type="button" className={`${btn} no-print`} disabled={act.isPending} onClick={() => act.mutate('close')}>Close batch</button>}
          {isEmpty && <button type="button" className={`${btn} no-print`} disabled={remove.isPending} onClick={confirmDelete}>{remove.isPending ? 'Deleting…' : 'Delete batch'}</button>}
        </div>
      </header>
      {act.isError && <p role="alert" className="text-radar-red">{errorText(act.error)}</p>}
      {remove.isError && <p role="alert" className="text-radar-red">{errorText(remove.error, 'delete the batch')}</p>}

      {isOpen && (
        <section className={`${panel} no-print`} aria-labelledby="next-h" onKeyDown={onEntryKey}>
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <h2 id="next-h" className={h2}>Next envelope</h2>
            <span className="text-[12px] text-paper/60"><kbd className={kbd}>Enter</kbd> saves and clears for the next one · <kbd className={kbd}>Esc</kbd> skips</span>
          </div>
          <div className="flex flex-wrap items-start gap-5">
            <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-2">
              <label className={lbl}>Who sent it
                <input ref={whoRef} autoFocus autoComplete="off" className={inp} value={raw} placeholder="Name, account no. or ZIP from the renewal form"
                  aria-controls="batch-hits"
                  onChange={(e) => { setRaw(e.target.value); if (picked) { setPicked(null); setLinesEdit(null); setAmountEdit(null) } }}
                  onKeyDown={onWhoKey} />
              </label>
              {ready && (
                <div id="batch-hits" className="flex flex-col gap-0.5 rounded-[10px] border border-ink-400 p-1">
                  {!current && <span className="px-3 py-2 text-[13px] text-paper/60" aria-live="polite">Searching…</span>}
                  {current && hits.length === 0 && <span className="px-3 py-2 text-[13px] text-paper/70">No one matches “{q}”.</span>}
                  {current && hits.map((h, i) => {
                    const hl = highlight(h.name, q)
                    return (
                      <button key={h.subscriber_id} type="button" aria-current={i === sel ? 'true' : undefined}
                        onMouseEnter={() => setSel(i)} onClick={() => choose(h)}
                        className={`flex min-h-[44px] w-full items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left ${focus} ${i === sel ? 'bg-ink-600' : 'hover:bg-ink-600'}`}>
                        <span className="flex flex-col gap-0.5">
                          <strong>{hl.pre}<mark className="bg-transparent text-amber-glow">{hl.hit}</mark>{hl.post} <span className="font-mono font-normal text-paper/60">{h.account_number ? `#${h.account_number}` : ''}</span></strong>
                          <span className="text-[12px] text-paper/70">{h.place}</span>
                        </span>
                        <span className="rounded bg-ink-600 px-1.5 py-0.5 text-[11px] font-bold text-paper/70">{h.titles.map((t) => t.abbr).join(' · ')}</span>
                      </button>
                    )
                  })}
                  <Link to={paymentHref('new', { batch: String(id) })} className={`px-3 py-2 text-[13px] font-semibold text-amber-glow ${focus}`}>Not listed: add a new member</Link>
                </div>
              )}
              {picked && (
                <div className="flex justify-between gap-3 rounded-[10px] bg-ink-600 p-3.5">
                  <span className="flex flex-col gap-1">
                    <strong className="text-[15px]">{picked.name} <span className="font-mono font-normal text-paper/60">{picked.account_number ? `#${picked.account_number}` : ''}</span></strong>
                    <span className="text-[13px] text-paper/70">{picked.place}</span>
                  </span>
                  <Link to={memberHref(picked.subscriber_id)} className={`whitespace-nowrap text-[13px] ${focus}`}>Open record</Link>
                </div>
              )}
            </div>

            <div className="flex min-w-0 flex-[2_1_460px] flex-col gap-3.5">
              <div className="flex flex-wrap items-end gap-3.5">
                <div className={lbl}>Method
                  <span role="radiogroup" aria-label="Method" className="inline-flex rounded-lg bg-ink-600 p-[3px]">
                    {methods.map((m) => (
                      <button key={m.method} type="button" role="radio" aria-checked={method === m.method} onClick={() => pickMethod(m.method)}
                        className={`min-h-[34px] rounded-md px-3 text-[13px] font-semibold ${focus} ${method === m.method ? 'bg-ink-800 text-paper ring-1 ring-ink-400' : 'text-paper/70'}`}>{m.label}</button>
                    ))}
                  </span>
                </div>
                {isManual(method) && (
                  <>
                    <label className={`${lbl} flex-[1_1_130px]`}>{METHODS.find((m) => m.method === method)!.ref}
                      <input ref={refRef} className={`${inp} font-mono`} value={reference} onChange={(e) => setReference(e.target.value)} />
                    </label>
                    <label className={`${lbl} flex-[1_1_120px]`}>Amount
                      <input ref={amountRef} className={`${inp} font-mono`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    </label>
                    {(() => {
                      const m: ManualMethod = method
                      const f = detailField(m)
                      return (
                        <label className={`${lbl} flex-[1_1_160px]`}>{f.label} (optional)
                          {f.kind === 'select'
                            ? <select className={inp} value={detail} onChange={(e) => setDetail(e.target.value)}>{f.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                            : <input className={inp} value={detail} placeholder={f.placeholder} onChange={(e) => setDetail(e.target.value)} />}
                        </label>
                      )
                    })()}
                  </>
                )}
                {method === 'bank' && (
                  <>
                    <label className={`${lbl} flex-[2_1_200px]`}>Name on the account
                      <input className={inp} autoComplete="off" value={nameOnAccount} onChange={(e) => setNameOnAccount(e.target.value)} />
                    </label>
                    <label className={`${lbl} flex-[1_1_150px]`}>Account type
                      <select className={inp} value={accountType} onChange={(e) => setAccountType(e.target.value)}>
                        {ACCOUNT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </label>
                  </>
                )}
                {!isManual(method) && (
                  <label className={`${lbl} flex-[1_1_120px]`}>Billing ZIP
                    <input className={`${inp} font-mono`} autoComplete="off" value={billZip} onChange={(e) => setBillZip(e.target.value)} />
                  </label>
                )}
              </div>
              {!isManual(method) && (
                <div className="flex flex-col gap-3">
                  <p className="text-[13px] leading-relaxed text-paper/70">
                    {method === 'card'
                      ? 'Key the card from the form. It goes straight to Authorize.Net and is charged the total of the lines below when you save.'
                      : 'Key the bank details from the form. They go straight to Authorize.Net; the account is debited the total of the lines below when you save. eChecks take a few days to settle, and the membership starts now.'}
                  </p>
                  {gw.data && <AcceptFields ref={acceptRef} instrument={method === 'card' ? 'card' : 'bank'} config={gw.data} disabled={save.isPending || !!unconfirmed || !!held} />}
                  {gw.isError && <p role="alert" className="text-[13px] text-radar-red">The card form could not be loaded.</p>}
                  {method === 'card' && (
                    <label className="inline-flex min-h-[32px] items-center gap-2 text-paper/70">
                      <input type="checkbox" checked={keepCard} onChange={(e) => setKeepCard(e.target.checked)} />
                      Keep this card and turn on auto-renew for these titles (the member asked for it on the form)
                    </label>
                  )}
                  {method === 'bank' && (
                    <label className="inline-flex min-h-[32px] items-center gap-2 rounded-lg bg-ink-600 p-3">
                      <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} />
                      The member signed the debit authorization on the form
                    </label>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <span className="text-[13px] font-semibold text-paper/70">Pays for <span className="font-normal text-paper/60">· filled in from their renewal, change if needed</span></span>
                {!picked && <span className="rounded-lg border border-ink-500 p-3 text-paper/60">Choose who sent it first</span>}
                {picked && lines.length === 0 && <span className="text-[13px] text-paper/70">{recordQ.isPending ? 'Looking up their titles…' : 'Nothing to fill in for this member. Add what the payment is for.'}</span>}
                {lines.map((l, i) => (
                  <div key={i} className="flex flex-col gap-3 rounded-lg border border-ink-500 p-3">
                    {l.for === 'gift' && <span className="text-[12px] font-bold uppercase tracking-widest text-amber-glow">Gift · {picked?.name ?? 'the payer'} pays</span>}
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] items-end gap-3">
                      <label className={lbl}>Title
                        <select className={inp} value={l.slug} onChange={(e) => pickTitle(i, e.target.value)}>
                          <option value="">Choose a title</option>
                          {titles.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
                        </select>
                      </label>
                      <label className={lbl}>Offer
                        <select className={inp} value={l.plan_id} disabled={!l.slug} onChange={(e) => pickPlan(i, l.slug, e.target.value)}>
                          {!l.plan_id && <option value="">Choose an offer</option>}
                          {plans.filter((p) => p.publication_slug === l.slug).map((p) => <option key={p.plan_id} value={p.plan_id}>{offerLabel(p.term_months)} · {dollars(p.amount_cents)}</option>)}
                        </select>
                      </label>
                      <span className="flex items-center justify-between gap-3 pb-2.5">
                        <span className="font-mono">{l.price_cents ? dollars(l.price_cents) : ''}</span>
                        {lines.length > 1 && <button type="button" className={btnSm} onClick={() => setLines((ls) => ls.filter((_, k) => k !== i))}>Remove</button>}
                      </span>
                    </div>
                    {l.for === 'gift' && l.rcpt && (
                      <RecipientPicker r={l.rcpt} set={(rcpt) => { setDupList(null); patchLine(i, { rcpt }) }} gate={gates[i]} />
                    )}
                  </div>
                ))}
                {picked && lines.length > 0 && <span className="text-[12px] text-paper/60">{lines.map((l) => titleName(plans, l.slug)).join(' + ')}</span>}
                <div className="flex flex-wrap gap-3.5 text-[13px]">
                  {picked && <button type="button" className={`font-semibold text-amber-glow underline-offset-2 hover:underline ${focus}`} onClick={() => setLines((ls) => [...ls, { slug: '', plan_id: '', price_cents: 0 }])}>+ Another title</button>}
                  {picked && <button type="button" className={`font-semibold text-amber-glow underline-offset-2 hover:underline ${focus}`} onClick={() => setLines((ls) => [...ls, { slug: '', plan_id: '', price_cents: 0, for: 'gift', rcpt: blankRcpt() }])}>+ A gift</button>}
                  {picked
                    ? <Link to={paymentHref(picked.subscriber_id, { batch: String(id) })} className={`text-paper/70 underline underline-offset-2 ${focus}`}>Open the full payment screen</Link>
                    : <span className="text-paper/60">For a new member, use the full payment screen.</span>}
                </div>
              </div>

              <div role="status" className={`rounded-lg px-3.5 py-2.5 font-semibold ${bal.ok ? 'bg-radar-green/10 text-radar-green' : 'bg-amber-wash text-amber-glow'}`}>
                {!picked ? 'Find who sent it to fill in what it pays for.' : bal.text}
              </div>
              {problem && <p role="alert" className="text-radar-red">{problem}</p>}
              {dupList && (
                <div role="alert" className="flex flex-col gap-3 rounded-xl border border-amber-glow/40 bg-amber-wash p-4">
                  <strong>A gift recipient may be someone we already have</strong>
                  {dupList.map((c, i) => (
                    <span key={`${c.subscriber_id}-${i}`} className="text-[13px]">{c.name} {c.account_number ? `#${c.account_number}` : ''} {c.zipcode ?? ''}</span>
                  ))}
                  <span className="text-[13px] text-paper/70">If one of them is the recipient, search their name again and choose the record.</span>
                  <button type="button" className={`${btn} self-start`} onClick={() => trySave(true)}>
                    These are all different people. Save again.
                  </button>
                </div>
              )}
              {recent && (
                <div role="alert" className="flex flex-col gap-3 rounded-xl border border-amber-glow/40 bg-amber-wash p-4">
                  <strong>{picked?.name ?? 'This member'} was charged a few minutes ago</strong>
                  <ul className="list-disc pl-5 text-[13px]">
                    {recent.map((c, i) => <li key={`${c.transaction_id ?? 'none'}-${i}`}>{recentChargeText(c)}</li>)}
                  </ul>
                  <span className="text-[13px] text-paper/70">Check the batch list below first: it has usually gone through. Charge again only if this is a second, separate form.</span>
                  <button type="button" className={`${btn} self-start`} onClick={() => trySave(false, true)}>Charge again anyway</button>
                </div>
              )}
              {held && (
                <p role="alert" className="rounded-lg bg-amber-wash p-3.5 text-amber-glow">
                  {held.message} <span className="text-paper/70">Transaction <strong className="font-mono">{held.transaction_id}</strong>.</span> Skip this one to go on with the batch.
                </p>
              )}
              {unconfirmed && (
                <p role="alert" className="rounded-lg bg-amber-wash p-3.5 text-amber-glow">
                  {unconfirmedText(method === 'bank' ? 'bank' : 'card', unconfirmed.txn, unconfirmed.invoice)}{' '}
                  {(unconfirmed.txn ?? unconfirmed.invoice) && <strong className="font-mono">{unconfirmed.txn ?? unconfirmed.invoice}</strong>} Skip this one to go on with the batch.
                </p>
              )}
              {saved && <p role="status" className="text-radar-green">{saved}</p>}
              <div className="flex flex-wrap gap-2">
                <button type="button" className={btnPrimary} disabled={!canSave || save.isPending} onClick={() => trySave()}>
                  {save.isPending ? (isManual(method) ? 'Saving…' : 'Charging…') : `${saveLabel(method, reference, isManual(method) ? amountCents : chargeCents)} and next`}
                </button>
                <button type="button" className={btn} onClick={clearEntry}>Skip this one</button>
              </div>
            </div>
          </div>
        </section>
      )}

      {isClosed && batch && (
        <section className={`${panel} print-sheet`} aria-labelledby="dep-h">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="dep-h" className={h2}>Deposit summary · batch {batch.label}</h2>
            <div className="no-print flex flex-wrap gap-2">
              <button type="button" className={btnSm} onClick={() => window.print()}>Print summary</button>
              {!batch.deposited_at && !isArchived && <button type="button" className={btnSm} disabled={act.isPending} onClick={() => act.mutate('reopen')}>Reopen batch</button>}
              {isArchived
                ? <button type="button" className={btnSm} disabled={act.isPending} onClick={() => act.mutate('unarchive')}>Unarchive</button>
                : <button type="button" className={btnSm} disabled={act.isPending} onClick={() => act.mutate('archive')}>Archive batch</button>}
            </div>
          </div>
          {summaryQ.isError && <p role="alert" className="text-radar-red">The summary could not be loaded.</p>}
          {summaryQ.data && (
            <>
              <div className="flex flex-wrap items-start gap-6">
                <table className="min-w-[280px] flex-1 border-collapse text-[14px]">
                  <thead><tr className="text-[11px] uppercase tracking-widest text-paper/60"><th scope="col" className="p-2 text-left">Method</th><th scope="col" className="p-2 text-right">Items</th><th scope="col" className="p-2 text-right">Amount</th></tr></thead>
                  <tbody>
                    {summaryQ.data.by_method.map((r) => (
                      <tr key={r.method} className="border-t border-ink-500"><td className="p-2 font-semibold">{METHOD_PLURAL[r.method] ?? r.method}</td><td className="p-2 text-right font-mono">{r.items}</td><td className="p-2 text-right font-mono">{dollars(r.amount_cents)}</td></tr>
                    ))}
                    <tr className="border-t border-ink-400 font-bold"><td className="p-2">Total</td><td className="p-2 text-right font-mono">{summaryQ.data.by_method.reduce((s, r) => s + r.items, 0)}</td><td className="p-2 text-right font-mono">{dollars(summaryQ.data.by_method.reduce((s, r) => s + r.amount_cents, 0))}</td></tr>
                  </tbody>
                </table>
                <table className="min-w-[280px] flex-1 border-collapse text-[14px]">
                  <thead><tr className="text-[11px] uppercase tracking-widest text-paper/60"><th scope="col" className="p-2 text-left">Title</th><th scope="col" className="p-2 text-right">Lines</th><th scope="col" className="p-2 text-right">Amount</th></tr></thead>
                  <tbody>
                    {summaryQ.data.by_title.map((r) => (
                      <tr key={r.publication_slug} className="border-t border-ink-500"><td className="p-2 font-semibold">{titleName(plans, r.publication_slug)}</td><td className="p-2 text-right font-mono">{r.lines}</td><td className="p-2 text-right font-mono">{dollars(r.amount_cents)}</td></tr>
                    ))}
                    <tr className="border-t border-ink-400 font-bold"><td className="p-2">Total</td><td className="p-2 text-right font-mono">{summaryQ.data.by_title.reduce((s, r) => s + r.lines, 0)}</td><td className="p-2 text-right font-mono">{dollars(summaryQ.data.by_title.reduce((s, r) => s + r.amount_cents, 0))}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="rounded-lg bg-radar-green/10 px-3.5 py-2.5 font-semibold text-radar-green">{depositSlipText(summaryQ.data)}</div>
              {(summaryQ.data.returned_after_deposit?.length ?? 0) > 0 && (
                <p role="note" className="rounded-lg bg-amber-wash px-3.5 py-2.5 text-[14px] text-amber-glow">{returnedAfterDepositText(summaryQ.data.returned_after_deposit ?? [])}</p>
              )}
            </>
          )}
          {!batch.deposited_at && <div className="no-print"><button type="button" className={btnPrimary} disabled={act.isPending} onClick={() => act.mutate('deposited')}>Mark as deposited</button></div>}
          {batch.deposited_at && <p className="text-paper/70">Marked as deposited.</p>}
        </section>
      )}

      <section className={`${panel} ${isClosed ? 'no-print' : ''}`} aria-labelledby="list-h">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <h2 id="list-h" className={h2}>In this batch</h2>
          <span className="text-[12px] text-paper/60">Newest first</span>
        </div>
        {batch && batch.entries.length === 0 && <p className="text-paper/70">Nothing entered yet.</p>}
        {batch && batch.entries.length > 0 && (
          <div className="relative overflow-x-auto">
            <table className="min-w-[760px] w-full border-collapse text-[14px]">
              <thead><tr className="text-[11px] uppercase tracking-widest text-paper/60">
                <th scope="col" className="p-2 text-left">#</th><th scope="col" className="p-2 text-left">Payer</th><th scope="col" className="p-2 text-left">Method</th>
                <th scope="col" className="p-2 text-left">Pays for</th><th scope="col" className="p-2 text-right">Amount</th><th scope="col" className="p-2"><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {batch.entries.map((e) => (
                  <tr key={e.n} className={`border-t border-ink-500 ${e.voided ? 'text-paper/50' : ''}`}>
                    <td className="p-2 font-mono text-paper/60">{e.n}</td>
                    <td className="p-2 font-semibold">{e.payer.name ?? ''} <span className="font-mono font-normal text-paper/60">{e.payer.account_number ? `#${e.payer.account_number}` : ''}</span></td>
                    <td className="p-2 text-paper/70">{METHOD_WORD[e.method] ?? e.method}{e.reference ? (e.via_gateway ? ` · txn ${e.reference}` : ` #${e.reference}`) : ''}</td>
                    <td className="p-2 text-paper/70">{e.lines_text}</td>
                    <td className="p-2 text-right font-mono">
                      <span className={e.voided ? 'line-through' : ''}>{dollars(e.voided ? e.original_amount_cents : e.amount_cents)}</span>
                      {e.partly_voided && <span className="block text-[11px] text-paper/60">of {dollars(e.original_amount_cents)} keyed</span>}
                      {!e.voided && !!e.refunded_cents && <span className="block text-[11px] text-paper/60">{dollars(e.refunded_cents)} refunded</span>}
                    </td>
                    <td className="p-2 text-right">
                      {entryVoidState(e) === 'voided' && <span className="text-[12px] font-semibold text-radar-red">Voided</span>}
                      {entryVoidState(e) === 'voided-after-deposit' && <span className="text-[12px] font-semibold text-amber-glow">Voided after deposit</span>}
                      {entryVoidState(e) === 'live' && <button type="button" className={`${btnSm} no-print`} onClick={() => setVoiding(e)}>Void</button>}
                      {entryVoidState(e) === 'refunded' && <span className="text-[12px] font-semibold text-radar-red">Refunded</span>}
                      {entryVoidState(e) === 'refundable' && <button type="button" className={`${btnSm} no-print`} onClick={() => setRefunding(e)}>Refund</button>}
                      {e.partly_voided && <span className="ml-2 text-[12px] font-semibold text-radar-red">Part voided</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {refunding && <MoneyDialog action="refund" item={refundItem(refunding)} onClose={() => setRefunding(null)} onDone={() => { setRefunding(null); refreshAfterVoid() }} />}
      {voiding && <VoidDialog entry={voiding} onClose={() => setVoiding(null)} onChanged={refreshAfterVoid} onDone={() => { setVoiding(null); refreshAfterVoid() }} />}
    </main>
  )
}

function VoidDialog({ entry, onClose, onDone, onChanged }: { entry: BatchEntry; onClose: () => void; onDone: () => void; onChanged: () => void }) {
  const [reason, setReason] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const boxRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { boxRef.current?.focus() }, [])

  async function submit() {
    if (busyRef.current || !reason.trim()) return
    busyRef.current = true; setBusy(true); setErr('')
    let done = 0
    try {
      for (const k of entry.order_keys) {
        try { await postVoid({ order_key: k, reason: reason.trim() }) } catch (e) {
          // A line voided earlier shows as 409; the rest still need voiding.
          if (!(e instanceof ApiError && e.status === 409)) throw e
        }
        done += 1
      }
      onDone()
    } catch (e) {
      onChanged()
      setErr(`${errorText(e, 'void')}${done > 0 ? ` ${done} of ${entry.order_keys.length} lines were voided. Try again to finish the rest.` : ''}`)
      busyRef.current = false; setBusy(false)
    }
  }

  return (
    <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}>
      <div role="dialog" aria-modal="true" aria-labelledby="void-h" className="flex w-full max-w-md flex-col gap-4 rounded-xl border border-ink-400 bg-ink-700 p-5">
        <h2 id="void-h" className="font-serif text-2xl">Void entry #{entry.n}?</h2>
        <p className="text-paper/70">{entry.payer.name} · {METHOD_WORD[entry.method] ?? entry.method}{entry.reference ? ` #${entry.reference}` : ''} · {dollars(entry.amount_cents)}. The memberships it paid for are taken back.</p>
        <label className={lbl}>Why is it being voided?
          <textarea ref={boxRef} className={`${inp} min-h-[80px] py-2`} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        {err && <p role="alert" className="text-radar-red">{err}</p>}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className={btn} onClick={onClose} disabled={busy}>Keep it</button>
          <button type="button" className={btnPrimary} disabled={!reason.trim() || busy} onClick={() => void submit()}>{busy ? 'Voiding…' : 'Void entry'}</button>
        </div>
      </div>
    </div>
  )
}
